<script setup>
import { computed } from 'vue';
import { format } from 'date-fns';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { buildWooCommerceOrderDetailsRoute } from '../../../helpers/navigation';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const { t, locale } = useI18n();
const route = useRoute();
const normalizedIntlLocale = computed(() =>
  locale.value?.toString().replace(/_/g, '-') || undefined
);

const formatDate = dateString => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'MMM d, yyyy');
};

const formatCurrency = (amount, currency) => {
  const numericAmount = Number(amount) || 0;
  try {
    return new Intl.NumberFormat(normalizedIntlLocale.value, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(numericAmount);
  } catch {
    return [amount || '—', currency || ''].filter(Boolean).join(' ');
  }
};

const getPaymentStatusClass = status => {
  const classes = { paid: 'bg-n-teal-5 text-n-teal-12' };
  return classes[status] || 'bg-n-solid-3 text-n-slate-12';
};

const orderStatus = computed(() => {
  const { status } = props.order;
  if (!status) return '';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(
    `CONVERSATION_SIDEBAR.WOOCOMMERCE.ORDER_STATUS.${status?.toUpperCase()}`
  );
});

const paymentStatus = computed(() => {
  const status = props.order.payment_status;
  if (!status) return '';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(
    `CONVERSATION_SIDEBAR.WOOCOMMERCE.PAYMENT_STATUS.${status?.toUpperCase()}`
  );
});

const orderDetailsRoute = computed(() => {
  const accountId = Number(route.params.accountId);
  if (!accountId || !props.order.record_id) {
    return null;
  }

  return buildWooCommerceOrderDetailsRoute(accountId, props.order.record_id);
});
</script>

<template>
  <div
    class="py-3 border-b border-n-weak last:border-b-0 flex flex-col gap-1.5"
  >
    <div class="flex justify-between items-center gap-2">
      <div class="font-medium flex min-w-0">
        <router-link
          v-if="orderDetailsRoute"
          :to="orderDetailsRoute"
          class="hover:underline text-n-slate-12 cursor-pointer truncate"
        >
          {{
            $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.ORDER_ID', {
              id: order.number || order.id,
            })
          }}
        </router-link>
        <span v-else class="truncate text-n-slate-12">
          {{
            $t('CONVERSATION_SIDEBAR.WOOCOMMERCE.ORDER_ID', {
              id: order.number || order.id,
            })
          }}
        </span>
      </div>
      <div
        :class="getPaymentStatusClass(order.payment_status)"
        class="text-xs px-2 py-1 rounded capitalize truncate"
        :title="paymentStatus"
      >
        {{ paymentStatus }}
      </div>
    </div>
    <div class="text-sm text-n-slate-12">
      <span
        v-if="formatDate(order.created_at)"
        class="text-n-slate-11 border-r border-n-weak pr-2"
      >
        {{ formatDate(order.created_at) }}
      </span>
      <span class="text-n-slate-11 pl-2">{{
        formatCurrency(order.total, order.currency)
      }}</span>
    </div>
    <div v-if="orderStatus">
      <span class="capitalize font-medium text-n-slate-11" :title="orderStatus">
        {{ orderStatus }}
      </span>
    </div>
  </div>
</template>
